import React, { useEffect, useRef, useState } from "react";
import { stateOptions, termOptions } from "../utils/options";
import { Controller, useForm } from "react-hook-form";
import { FaExclamationCircle } from "react-icons/fa";
import Select from "react-select";
import { yupResolver } from "@hookform/resolvers/yup";
import { validationSchema } from "../utils/validationSchema";
import DataModal from "./DataModal";
import ToolTip from "./ToolTip";
import { IoMdInformationCircleOutline } from "react-icons/io";
import {
  buildReviewURL,
  formatDateString,
  getStateObject,
  getTermObject,
} from "../utils/helperFunctions";
import { useDispatch, useSelector } from "react-redux";
import { setLoading } from "../Redux/Loader/loadingSlice";
import { useNavigate } from "react-router-dom";
import { updateExhibit } from "../firebase.config";
import { Autocomplete, useJsApiLoader } from "@react-google-maps/api";
import { fillForm } from "../utils/fillForm";
import { toMDY } from "../utils/inputValidations";

const EditForm = ({
  setExhibitData,
  currentDate,
  setFileUploads,
  isPartner,
  exhibit,
}) => {
  const [formState, setFormState] = useState(null);
  const [errorOpen, setErrorOpen] = useState(false);
  const [overrideState, setOverrideState] = useState({});
  const [fileUrls, setFileUrls] = useState(exhibit.fileUrls || []);
  const [imagesDirty, setImagesDirty] = useState(false);
  const [newFiles, setNewFiles] = useState([]);
  const [ghostName] = useState(
    () => `q_${Math.random().toString(36).slice(2)}`
  );
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [addrSearch, setAddrSearch] = useState("");
  const user = useSelector((state) => state.user);
  const errorsArr = [];
  const dispatch = useDispatch();
  // allow past + future, but clamp to a reasonable window
  const MIN_START = `${new Date().getFullYear() - 5}-01-01`; // 01/01/(currentYear+5)
  const MAX_START = `${new Date().getFullYear() + 5}-12-31`; // 12/31/(currentYear+5)

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ["places"],
  });

  const [auto, setAuto] = useState(null);
  const locationAddrRef = useRef(null);
  // NEW: session + sqft-average helpers
  const askedRef = useRef(new Set());
  const pendingSubmitRef = useRef(null);
  const lastSqftRef = useRef(null);
  const lastDesksRef = useRef(null);
  const lastSqftNoRef = useRef(null);

  const clearOverride = (key) =>
    setOverrideState((prev) => (prev[key] ? { ...prev, [key]: false } : prev));

  // blur-only: queue the sqft modal (even if another modal is open)

  const enqueueSqftConfirm = () => {
    // snapshot current numbers into formState so DataModal can compute safely
    const sqft = Number(getValues("sqftLeased"));
    const desks = Number(getValues("desks"));
    setFormState((prev) => ({ ...(prev || {}), sqftLeased: sqft, desks }));

    setErrorOpen((prev) => {
      const current = Array.isArray(prev) ? prev : [];
      if (current.includes("sqftLeased")) return prev;
      askedRef.current.delete("sqftLeased"); // allow re-ask again
      return [...current, "sqftLeased"];
    });
  };

  // blur-only: re-evaluate average and (re)queue when changed and still high
  const checkSqftAverageOnChange = () => {
    const sqft = Number(getValues("sqftLeased"));
    const desks = Number(getValues("desks"));

    const changed =
      sqft !== lastSqftRef.current || desks !== lastDesksRef.current;
    if (!changed) return;

    // user edited → clear previous "No" snapshot
    lastSqftNoRef.current = null;

    lastSqftRef.current = sqft;
    lastDesksRef.current = desks;

    if (sqft && desks > 0 && sqft / desks >= 200) {
      clearOverride("sqftLeased");
      askedRef.current.delete("sqftLeased");
      enqueueSqftConfirm();
    }
  };

  // submit-time confirmations (NO sqft average here)
  const buildAttestations = (data) => {
    const keys = [];

    if (!data.facility.includes("cam")) keys.push("cam");
    if (!data.facility.includes("tax")) keys.push("tax");

    if (data.sqftLeased >= 500 && data.amenity.includes("meeting"))
      keys.push("meeting");
    if (data.sqftLeased >= 500 && data.amenity.includes("kitchen"))
      keys.push("kitchen");
    if (data.sqftLeased >= 500 && data.amenity.includes("furnished"))
      keys.push("furnished");
    if (data.sqftLeased >= 500 && data.amenity.includes("copy"))
      keys.push("copy");
    if (data.sqftLeased >= 500 && data.amenity.includes("receptionist"))
      keys.push("receptionist");

    if (data.desks >= 2) keys.push("desks");

    return [...new Set(keys)];
  };

  // When a modal step gets "No", patch the RHF values (mirrors Form.jsx behavior)
  const handleModalNo = (key) => {
    const facility = getValues("facility") || [];
    const amenity = getValues("amenity") || [];

    switch (key) {
      case "cam":
      case "tax": {
        const next = facility.includes(key) ? facility : [...facility, key];
        setValue("facility", next, { shouldDirty: true, shouldValidate: true });
        break;
      }
      case "meeting":
      case "kitchen":
      case "furnished":
      case "copy":
      case "receptionist": {
        const next = amenity.filter((v) => v !== key);
        setValue("amenity", next, { shouldDirty: true, shouldValidate: true });
        break;
      }
      case "desks":
      case "sqftLeased": {
        const sqft = Number(getValues("sqftLeased"));
        const desks = Number(getValues("desks"));
        // snapshot the "No" values so we can block submit if unchanged and still high
        lastSqftNoRef.current = { sqft, desks };
        break;
      }
      default:
        break;
    }

    // force re-ask next submit unless user fixes it
    setOverrideState((prev) => ({ ...prev, [key]: false }));
    askedRef.current.delete(key);
  };

  // helper to get a component by type
  const pick = (components, type) => {
    const c = components?.find((x) => x.types?.includes(type));
    return c
      ? { long: c.long_name, short: c.short_name }
      : { long: "", short: "" };
  };

  useEffect(() => {
    // initialize from any existing RHF value
    setAddrSearch(getValues("locationAddress") || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePlaceChanged = () => {
    if (!auto) return;
    const place = auto.getPlace();
    const comps = place?.address_components || [];

    const streetNo = pick(comps, "street_number").long;
    const route = pick(comps, "route").long;
    const line1 = [streetNo, route].filter(Boolean).join(" ");

    const city =
      pick(comps, "locality").long ||
      pick(comps, "sublocality").long ||
      pick(comps, "administrative_area_level_3").long;

    const stateShort = pick(comps, "administrative_area_level_1").short;
    const zip = pick(comps, "postal_code").long;

    setValue("locationAddress", line1, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue("city", city, { shouldDirty: true, shouldValidate: true });
    setValue("zipcode", zip, { shouldDirty: true, shouldValidate: true });

    const stateOpt =
      stateOptions.find((o) => o.value === stateShort) ||
      (stateShort ? { value: stateShort, label: stateShort } : null);
    if (stateOpt)
      setValue("state", stateOpt, { shouldDirty: true, shouldValidate: true });

    setAddrSearch(line1);

    if (locationAddrRef.current) {
      setTimeout(() => {
        locationAddrRef.current.value = line1;
      }, 0);
    }
  };

  const removeImage = (urlToRemove) => {
    setFileUrls(fileUrls.filter((url) => url !== urlToRemove));
    setImagesDirty(true); // NEW
  };

  // Normalizes various date inputs to an <input type="date"> value (YYYY-MM-DD).
  const toDateInputValue = (value) => {
    if (!value) return "";
    try {
      if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return value;
      }
      if (typeof value === "string") {
        const m = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/);
        if (m) {
          const mm = String(m[1]).padStart(2, "0");
          const dd = String(m[2]).padStart(2, "0");
          const yyyy = m[3].length === 2 ? `20${m[3]}` : m[3];
          return `${yyyy}-${mm}-${dd}`;
        }
      }
      const d = new Date(value);
      if (!Number.isNaN(d.getTime())) {
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}`;
      }
    } catch (_) {}
    return "";
  };

  const handleChange = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach((f) => (f.id = Math.random()));
    setFileUploads((prev) => [...prev, ...files]); // if you still need this for elsewhere
    setNewFiles((prev) => [...prev, ...files]); // ← used by onSubmit
    setImagesDirty(true);
  };

  const handleClearAll = () => {
    // Define what an "empty" form looks like
    const emptyValues = {
      serviceProvider: "",
      commonName: "",
      locationAddress: "",
      city: "",
      state: null,
      zipcode: "",
      sqftLeased: "",
      term: null,
      desks: "",
      propertyType: "",
      spaceType: "",
      settingType: "",
      facility: [],
      utility: [],
      amenity: [],
      startDate: "",
      description: "",
    };

    // Clear all RHF fields to empty, and clear dirty/touched flags
    reset(emptyValues, {
      keepDirty: false,
      keepTouched: false,
    });

    // Clear the visible address search box
    setAddrSearch("");

    // Also clear your local form helpers
    setFormState(null);
    setOverrideState({});
    setErrorOpen(false);
    askedRef.current.clear();
    lastSqftRef.current = null;
    lastDesksRef.current = null;
    lastSqftNoRef.current = null;

    // Clear images / uploads
    setFileUrls([]);
    setImagesDirty(false);
    if (typeof setFileUploads === "function") {
      setFileUploads([]);
    }
    setNewFiles([]);

    // Scroll to top
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  };

  // REPLACE your handleInputBlur with this:
  const handleInputBlur = (e) => {
    const { name, value } = e.target;
    const numericNames = new Set(["sqftLeased", "desks"]);
    setFormState((prev) => ({
      ...prev,
      [name]: numericNames.has(name) ? Number(value) : value,
    }));
  };

  // NEW: seed last-known values from defaults so first blur comparison works
  useEffect(() => {
    const initSqft = Number(getValues("sqftLeased") || 0);
    const initDesks = Number(getValues("desks") || 0);
    lastSqftRef.current = initSqft;
    lastDesksRef.current = initDesks;
    // also prime formState so DataModal has both values before first modal
    setFormState((prev) => ({
      ...(prev || {}),
      sqftLeased: initSqft,
      desks: initDesks,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputFocus = (e) => {
    let { name } = e.target;
    if (name === "desks") name = "sqftLeased";
    if (overrideState[name]) {
      setOverrideState((prev) => ({ ...prev, [name]: false }));
    }
  };
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isDirty, isSubmitting },
    getValues,
    setValue,
    reset,
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      ...exhibit,
      startDate: toDateInputValue(exhibit?.startDate), // stays YYYY-MM-DD for <input type="date">
      state: getStateObject(exhibit?.state),
      term: getTermObject(exhibit?.term),
    },
  });

  const { ref: rhfAddrRef, ...addrReg } = register("locationAddress");

  useEffect(() => {
    setFileUrls(exhibit.fileUrls || []);
    setImagesDirty(false);
  }, [exhibit?.fileUrls]);

  useEffect(() => {
    if (!exhibit) return;
    console.log("%c[EditForm] reset with exhibit", "color:#777", exhibit);
    reset({
      ...exhibit,
      startDate: toDateInputValue(exhibit?.startDate),
      state: getStateObject(exhibit?.state),
      term: getTermObject(exhibit?.term),
    });
  }, [exhibit, reset]);

  const navigate = useNavigate();

  const goToDashboardAfterSave = () => {
    dispatch(setLoading(true));
    navigate("/dashboard", { replace: true, state: { fromSave: true } });
  };

  const imagesChanged = React.useMemo(() => {
    const a = (exhibit.fileUrls || []).slice().sort();
    const b = (fileUrls || []).slice().sort();
    if (a.length !== b.length) return true;
    return a.some((v, i) => v !== b[i]);
  }, [exhibit.fileUrls, fileUrls]);

  const hasUnsavedChanges = isDirty || imagesDirty || imagesChanged;
  const saveExitLabel = hasUnsavedChanges ? "Save & Exit" : "Back";

  const onSubmit = async (data, status) => {
    dispatch(setLoading(true));
    try {
      const stateValue = data.state && data.state.value ? data.state.value : "";

      // Base payload (no id here; we add it only when needed)
      const baseExhibit = {
        serviceProvider: data.serviceProvider ?? "",
        commonName: data.commonName ?? "",
        locationAddress: data.locationAddress ?? "",
        city: data.city ?? "",
        state: stateValue,
        zipcode: data.zipcode ?? "",
        sqftLeased: data.sqftLeased ?? "",
        term: data.term?.label || "",
        desks: data.desks ?? "",
        propertyType: data.propertyType ?? "",
        spaceType: data.spaceType ?? "",
        settingType: data.settingType ?? "",
        facility: data.facility ?? [],
        utility: data.utility ?? [],
        amenity: data.amenity ?? [],
        startDate: data.startDate ? formatDateString(data.startDate) : "",
        description: data.description ?? "",
        createdDate: exhibit.createdDate,
        status,
        fileUrls, // current kept images
      };

      // ---------- SUBMIT-TIME confirmations (modeled after Form.jsx)
      if (status !== "Saved") {
        // base keys (NO sqft average here)
        let toConfirm = buildAttestations(data).filter(
          (k) => !overrideState[k] && !askedRef.current.has(k)
        );

        // sqft-per-desk: include on submit IF still high AND not previously affirmed "Yes"
        const sqft = Number(data.sqftLeased);
        const desks = Number(data.desks);
        const ratioHigh = sqft && desks > 0 && sqft / desks >= 200;

        if (ratioHigh && overrideState.sqftLeased !== true) {
          toConfirm = [
            "sqftLeased",
            ...toConfirm.filter((k) => k !== "sqftLeased"),
          ];
        }

        if (toConfirm.length) {
          pendingSubmitRef.current = { data, status };
          setFormState((prev) => ({ ...prev, ...baseExhibit }));
          setErrorOpen(Array.from(new Set(toConfirm)));
          toConfirm.forEach((k) => askedRef.current.add(k));
          dispatch(setLoading(false));
          return false;
        }
      }
      // ---------- END confirmations

      // ---------- PROCEED with file/PDF + Firestore update
      let pdfUrlFromFill, fileUrlsFromFill;
      if (typeof fillForm === "function") {
        const result = await fillForm(
          { ...baseExhibit, id: exhibit.id },
          status,
          {
            userCompany: user?.companyName || "",
            signerName: `${user?.contactFirstName || ""} ${
              user?.contactLastName || ""
            }`.trim(),
            signedDate: currentDate,

            companyName: user?.companyName || "",
            serviceProvider: baseExhibit.serviceProvider,
            exhibitId: exhibit.id,
            previousPdfUrl: exhibit.pdfURL,

            newImageFiles: newFiles || [],
            keepImageUrls: fileUrls,
            previousImageUrls: exhibit.fileUrls || [],
          }
        );
        pdfUrlFromFill = result?.pdfURL;
        fileUrlsFromFill = result?.fileUrls;
      }

      const finalPayload = {
        ...baseExhibit,
        fileUrls: fileUrlsFromFill ?? baseExhibit.fileUrls,
        ...(pdfUrlFromFill ? { pdfURL: pdfUrlFromFill } : {}),
      };

      await updateExhibit({ id: exhibit.id, ...finalPayload });

      if (typeof setExhibitData === "function") {
        setExhibitData((prev) => ({
          ...(prev || {}),
          ...finalPayload,
          updatedAt: new Date().toISOString(),
        }));
      }

      askedRef.current.clear();
      setErrorOpen(false);
      if (typeof setNewFiles === "function") setNewFiles([]);
      return true;
    } catch (e) {
      console.error("[EditForm] onSubmit failed:", e);
      return false;
    } finally {
      dispatch(setLoading(false));
    }
  };

  return (
    <>
      {errorOpen && Array.isArray(errorOpen) && errorOpen.length > 0 && (
        <DataModal
          setErrorOpen={setErrorOpen}
          errorOpen={errorOpen}
          formState={formState}
          setFormState={setFormState}
          setOverrideState={setOverrideState}
          onNo={handleModalNo}
          onComplete={async ({ allYes } = {}) => {
            // Capture which keys were asked
            const openKeys = Array.isArray(errorOpen) ? errorOpen.slice() : [];

            // We always close the modal here
            setErrorOpen(false);

            // If they clicked "Yes & Submit", mark all asked keys as affirmed
            if (allYes && openKeys.length) {
              setOverrideState((prev) => ({
                ...prev,
                ...openKeys.reduce((acc, k) => ((acc[k] = true), acc), {}),
              }));
            }

            // Re-run the pending submit (what the user originally tried)
            if (!pendingSubmitRef.current) return;
            const { data, status } = pendingSubmitRef.current;
            pendingSubmitRef.current = null;

            // Try the submit again now that overrides are applied
            const ok = allYes ? await onSubmit(data, status) : false;

            // If this was a Save & Continue flow and it succeeded, navigate now
            if (ok && status === "In Progress") {
              const nav = buildReviewURL(
                {
                  id: exhibit.id,
                  companyName: exhibit.companyName,
                  commonName: exhibit.commonName,
                  serviceProvider: exhibit.serviceProvider,
                },
                true
              );
              navigate(nav.path, { state: nav.state });
            }
          }}
        />
      )}

      <div className="exhibit-form-container">
        <div>
          <h1 className="new-exhibit-h1">
            Office Lease and Other Real Estate Services
          </h1>
          <h4>(complete one form per location address)</h4>
        </div>

        <form
          className="new-exhibit-form"
          onSubmit={(e) => e.preventDefault()}
          noValidate
          autoComplete="off"
        >
          {/* Autofill decoys so Chrome fills these instead of your visible box */}
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              inset: 0,
              width: 0,
              height: 0,
              overflow: "hidden",
            }}
          >
            <input
              type="text"
              name="street-address"
              autoComplete="street-address"
              tabIndex={-1}
              style={{ opacity: 0, width: 1, height: 1 }}
            />
            <input
              type="text"
              name="address-line2"
              autoComplete="address-line2"
              tabIndex={-1}
              style={{ opacity: 0, width: 1, height: 1 }}
            />
            <input
              type="text"
              name="address-level2"
              autoComplete="address-level2"
              tabIndex={-1}
              style={{ opacity: 0, width: 1, height: 1 }}
            />
            <input
              type="text"
              name="address-level1"
              autoComplete="address-level1"
              tabIndex={-1}
              style={{ opacity: 0, width: 1, height: 1 }}
            />
            <input
              type="text"
              name="postal-code"
              autoComplete="postal-code"
              tabIndex={-1}
              style={{ opacity: 0, width: 1, height: 1 }}
            />
          </div>

          <div
            className={
              errors.serviceProvider
                ? "job-form-input-container error"
                : "job-form-input-container"
            }
          >
            {errors.serviceProvider && (
              <p className="error-message">
                <span style={{ marginRight: "5px" }}>
                  <FaExclamationCircle />
                </span>
                {errors.serviceProvider.message}
              </p>
            )}
            <div className="job-form-input">
              <label htmlFor="serviceProvider">
                Service Provider (Lessor/Landlord)
              </label>
              <input
                id="serviceProvider"
                type="text"
                cols={35}
                placeholder=""
                className="input"
                {...register("serviceProvider")}
              />
            </div>
          </div>

          <div
            className={
              errors.commonName
                ? "job-form-input-container error"
                : "job-form-input-container"
            }
          >
            {errors.commonName && (
              <p className="error-message">
                <span style={{ marginRight: "5px" }}>
                  <FaExclamationCircle />
                </span>
                {errors.commonName.message}
              </p>
            )}
            <div className="job-form-input">
              <label htmlFor="commonName">Common/Branded Name</label>
              <input
                id="commonName"
                type="text"
                cols={35}
                placeholder=""
                className="input"
                {...register("commonName")}
              />
            </div>
          </div>

          <div
            className={
              errors.locationAddress
                ? "job-form-input-container error"
                : "job-form-input-container"
            }
          >
            {errors.locationAddress && (
              <p className="error-message">
                <span style={{ marginRight: "5px" }}>
                  <FaExclamationCircle />
                </span>
                {errors.locationAddress.message}
              </p>
            )}

            <div className="job-form-input">
              {/* keep same label/id for styling/analytics */}
              <label htmlFor="locationAddress">Location Address</label>

              {isLoaded ? (
                <Autocomplete
                  onLoad={setAuto}
                  onPlaceChanged={handlePlaceChanged}
                  options={{
                    fields: ["address_components"],
                    types: ["address"],
                    componentRestrictions: { country: ["us"] },
                  }}
                >
                  {/* Visible search box (NOT registered with RHF) */}
                  <input
                    id="locationAddress" // same id to keep styling/QA hooks
                    name={ghostName} // random name to dodge autofill heuristics
                    type="text"
                    className="input"
                    value={addrSearch}
                    onChange={(e) => setAddrSearch(e.target.value)}
                    autoComplete="new-password" // stronger than "off" for Chrome
                    inputMode="search"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    form="__noform" // detach from real form submission
                    readOnly // guard ON by default
                    onPointerDown={(e) => {
                      const el = e.currentTarget;
                      if (el.readOnly) {
                        e.preventDefault(); // block Chrome’s autofill popup
                        el.focus();
                        requestAnimationFrame(() => {
                          el.readOnly = false;
                        });
                      }
                    }}
                    onFocus={(e) => {
                      const el = e.currentTarget;
                      if (el.readOnly) {
                        requestAnimationFrame(() => {
                          el.readOnly = false;
                        });
                      }
                    }}
                    onBlur={(e) => {
                      e.currentTarget.readOnly = true;
                    }} // re-arm guard
                    onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
                    data-lpignore="true" // ask password managers to ignore
                    data-1p-ignore="true"
                    ref={locationAddrRef}
                  />
                </Autocomplete>
              ) : (
                // Fallback while Maps script loads
                <input
                  id="locationAddress"
                  name={ghostName}
                  type="text"
                  className="input"
                  value={addrSearch}
                  onChange={(e) => setAddrSearch(e.target.value)}
                  autoComplete="new-password"
                  inputMode="search"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  form="__noform"
                  readOnly
                  onPointerDown={(e) => {
                    const el = e.currentTarget;
                    if (el.readOnly) {
                      e.preventDefault();
                      el.focus();
                      requestAnimationFrame(() => {
                        el.readOnly = false;
                      });
                    }
                  }}
                  onFocus={(e) => {
                    const el = e.currentTarget;
                    if (el.readOnly) {
                      requestAnimationFrame(() => {
                        el.readOnly = false;
                      });
                    }
                  }}
                  onBlur={(e) => {
                    e.currentTarget.readOnly = true;
                  }}
                  onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
                  ref={locationAddrRef}
                />
              )}

              {/* The real RHF field (hidden) that stores just "123 Main St" */}
              <input type="hidden" {...register("locationAddress")} />
            </div>
          </div>

          <div className="city-state-zip">
            <div
              className={
                errors.city
                  ? "job-form-input-container error"
                  : "job-form-input-container"
              }
            >
              {errors.city && (
                <p className="error-message">
                  <span style={{ marginRight: "5px" }}>
                    <FaExclamationCircle />
                  </span>
                  {errors.city.message}
                </p>
              )}
              <div className="job-form-input">
                <label htmlFor="city">City</label>
                <input
                  id="city"
                  type="text"
                  cols={35}
                  placeholder=""
                  className="input"
                  {...register("city")}
                />
              </div>
            </div>

            <div
              className={
                errors.state
                  ? "job-form-input-container error"
                  : "job-form-input-container"
              }
            >
              {errors.state && (
                <p className="error-message">
                  <span style={{ marginRight: "5px" }}>
                    <FaExclamationCircle />
                  </span>
                  {errors.state.message}
                </p>
              )}
              <div className="job-form-input">
                <label htmlFor="state">State</label>
                <Controller
                  name="state"
                  control={control}
                  defaultValue={null}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <Select
                      id="state"
                      className="select-input"
                      placeholder="Select state"
                      options={stateOptions}
                      onChange={(selectedOption) => onChange(selectedOption)}
                      isClearable
                      value={value}
                      onBlur={onBlur}
                    />
                  )}
                />
              </div>
            </div>

            <div
              className={
                errors.zipcode
                  ? "job-form-input-container error"
                  : "job-form-input-container"
              }
            >
              {errors.zipcode && (
                <p className="error-message">
                  <span style={{ marginRight: "5px" }}>
                    <FaExclamationCircle />
                  </span>
                  {errors.zipcode.message}
                </p>
              )}
              <div className="job-form-input">
                <label htmlFor="zipcode">Zip</label>
                <input
                  id="zipcode"
                  type="text"
                  cols={35}
                  placeholder=""
                  className="input"
                  {...register("zipcode")}
                />
              </div>
            </div>
          </div>

          <div
            className={
              errors.sqftLeased
                ? "job-form-input-container error"
                : "job-form-input-container"
            }
          >
            {errors.sqftLeased && (
              <p className="error-message">
                <span style={{ marginRight: "5px" }}>
                  <FaExclamationCircle />
                </span>
                {errors.sqftLeased.message}
              </p>
            )}
            <div className="job-form-input sqft">
              <label htmlFor="sqftLeased">
                Square Feet Leased
                <ToolTip text="The amount of space dedicated to the Lessee's employees and should never include building common areas such as hallways, lobbies, and restrooms, or space shared with the lessor or other tenants such as conference rooms, kitchen/break areas, and reception areas.">
                  <IoMdInformationCircleOutline />
                </ToolTip>
              </label>
              <input
                id="sqftLeased"
                type="number"
                placeholder=""
                {...register("sqftLeased", { valueAsNumber: true })}
                onBlur={(e) => {
                  handleInputBlur(e);
                  clearOverride("sqftLeased");
                  askedRef.current.delete("sqftLeased");
                  checkSqftAverageOnChange();
                }}
                onFocus={handleInputFocus}
              />
            </div>
          </div>

          <div className="job-form-input-container">
            <div className="job-form-input">
              <label htmlFor="description">Additional Description</label>
              <textarea
                id="description"
                rows={2}
                placeholder=""
                className="input"
                cols={35}
                {...register("description")}
              />
            </div>
          </div>

          <div className="startdate-term">
            <div
              className={
                errors.startDate
                  ? "job-form-input-container error"
                  : "job-form-input-container"
              }
            >
              {errors.startDate && (
                <p className="error-message">
                  <span style={{ marginRight: "5px" }}>
                    <FaExclamationCircle />
                  </span>
                  {errors.startDate.message}
                </p>
              )}
              <div className="job-form-input">
                <label htmlFor="startDate">Expected Start Date</label>
                <input
                  id="startDate"
                  type="date"
                  className="input"
                  placeholder="mm/dd/yyyy"
                  min={MIN_START}
                  max={MAX_START}
                  {...register("startDate", { valueAsDate: true })}
                />
              </div>
            </div>

            <div
              className={
                errors.term
                  ? "job-form-input-container error"
                  : "job-form-input-container"
              }
            >
              {errors.term && (
                <p className="error-message">
                  <span style={{ marginRight: "5px" }}>
                    <FaExclamationCircle />
                  </span>
                  {errors.term.message}
                </p>
              )}
              <div className="job-form-input">
                <label style={{ width: "170px" }} htmlFor="term">
                  Expected Term
                </label>
                <Controller
                  name="term"
                  control={control}
                  defaultValue={null}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <Select
                      id="term"
                      className="select-input"
                      placeholder="Select Term"
                      options={termOptions}
                      onChange={(selectedOption) => onChange(selectedOption)}
                      value={value}
                      onBlur={onBlur}
                      isClearable
                    />
                  )}
                />
              </div>
            </div>
          </div>

          <div
            className={
              errors.desks
                ? "job-form-input-container error"
                : "job-form-input-container"
            }
          >
            {errors.desks && (
              <p className="error-message">
                <span style={{ marginRight: "5px" }}>
                  <FaExclamationCircle />
                </span>
                {errors.desks.message}
              </p>
            )}
            <div className="job-form-input">
              <label htmlFor="desks">
                Number of Desks/Workstations in the Leased Space
                <ToolTip text="The number of workstations or desks that the Lessee's employees will occupy and is used to estimate the value of amenities such as furniture, internet access, and phone lines, when these amenities are included in the lease.">
                  <IoMdInformationCircleOutline />
                </ToolTip>
              </label>
              <input
                id="desks"
                type="number"
                placeholder=""
                {...register("desks", { valueAsNumber: true })}
                onBlur={(e) => {
                  handleInputBlur(e);
                  clearOverride("sqftLeased");
                  askedRef.current.delete("sqftLeased");
                  checkSqftAverageOnChange();
                }}
                onFocus={handleInputFocus}
              />
            </div>
          </div>

          <div className="check-header-container">
            <p className="checkbox-header">{`Space Descriptions: `}</p>
            <span className="space-extra">
              {`[provide any photos, floorplans, or other information as necessary]`}
            </span>
          </div>

          <div className="checkbox-outer-container">
            <div
              className={
                errors.propertyType
                  ? "property-type-container error"
                  : "property-type-container"
              }
            >
              {errors.propertyType && (
                <p className="error-message">
                  <span style={{ marginRight: "5px" }}>
                    <FaExclamationCircle />
                  </span>
                  {errors.propertyType.message}
                </p>
              )}
              <p className="checkbox-heading">Property Type</p>
              <Controller
                name="propertyType"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <div>
                    <label className="radio-container" htmlFor="prop-office">
                      Office
                      <div className="job-form-input-radio-check">
                        <input
                          id="prop-office"
                          type="radio"
                          value="Office"
                          checked={field.value === "Office"}
                          onChange={() => field.onChange("Office")}
                        />
                        <span className="checkmark"></span>
                      </div>
                    </label>
                    <label
                      className="radio-container"
                      htmlFor="prop-industrial"
                    >
                      Industrial/Warehouse
                      <div className="job-form-input-radio-check">
                        <input
                          id="prop-industrial"
                          type="radio"
                          value="Industrial/Warehouse"
                          checked={field.value === "Industrial/Warehouse"}
                          onChange={() =>
                            field.onChange("Industrial/Warehouse")
                          }
                        />
                        <span className="checkmark"></span>
                      </div>
                    </label>
                    <label className="radio-container" htmlFor="prop-retail">
                      Retail
                      <div className="job-form-input-radio-check">
                        <input
                          id="prop-retail"
                          type="radio"
                          value="Retail"
                          checked={field.value === "Retail"}
                          onChange={() => field.onChange("Retail")}
                        />
                        <span className="checkmark"></span>
                      </div>
                    </label>
                    <label className="radio-container" htmlFor="prop-mix">
                      Mixed/Other
                      <div className="job-form-input-radio-check">
                        <input
                          id="prop-mix"
                          type="radio"
                          value="Mixed/Other"
                          checked={field.value === "Mixed/Other"}
                          onChange={() => field.onChange("Mixed/Other")}
                        />
                        <span className="checkmark"></span>
                      </div>
                    </label>
                  </div>
                )}
              />
            </div>

            <div
              className={
                errors.spaceType
                  ? "space-type-container error"
                  : "space-type-container"
              }
            >
              {errors.spaceType && (
                <p className="error-message">
                  <span style={{ marginRight: "5px" }}>
                    <FaExclamationCircle />
                  </span>
                  {errors.spaceType.message}
                </p>
              )}
              <p className="checkbox-heading">Space Type</p>
              <Controller
                name="spaceType"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <div>
                    <label className="radio-container" htmlFor="space-private">
                      Private office(s)
                      <div className="job-form-input-radio-check">
                        <input
                          id="space-private"
                          type="radio"
                          value="Private office"
                          checked={field.value === "Private office"}
                          onChange={() => field.onChange("Private office")}
                        />
                        <span className="checkmark"></span>
                      </div>
                    </label>
                    <label className="radio-container" htmlFor="space-cube">
                      Cubicle(s)/Other
                      <div className="job-form-input-radio-check">
                        <input
                          id="space-cube"
                          type="radio"
                          value="Cubicle"
                          checked={field.value === "Cubicle"}
                          onChange={() => field.onChange("Cubicle")}
                        />
                        <span className="checkmark"></span>
                      </div>
                    </label>
                  </div>
                )}
              />
            </div>

            <div
              className={
                errors.settingType
                  ? "setting-type-container error"
                  : "setting-type-container"
              }
            >
              {errors.settingType && (
                <p className="error-message">
                  <span style={{ marginRight: "5px" }}>
                    <FaExclamationCircle />
                  </span>
                  {errors.settingType.message}
                </p>
              )}
              <p className="checkbox-heading">
                Setting
                <ToolTip
                  direction="left"
                  text={`Can be Interior or Exterior/Window. Exterior/Window should only be checked when the office is located on the perimeter of the building and has a window with an outside view. If the office is located on the interior of the building and has a window, then select "Interior". If "Interior" and "Exterior/Window" are checked, we will value the space as an interior office. If there are multiple offices being subleased, and they have different settings, please elaborate in the "Additional Description".`}
                >
                  <IoMdInformationCircleOutline />
                </ToolTip>
              </p>

              <Controller
                name="settingType"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <div>
                    <label
                      className="radio-container"
                      htmlFor="setting-interior"
                    >
                      Interior
                      <div className="job-form-input-radio-check">
                        <input
                          id="setting-interior"
                          type="radio"
                          value="Interior"
                          checked={field.value === "Interior"}
                          onChange={() => field.onChange("Interior")}
                        />
                        <span className="checkmark"></span>
                      </div>
                    </label>
                    <label className="radio-container" htmlFor="setting-window">
                      Exterior/Window
                      <div className="job-form-input-radio-check">
                        <input
                          id="setting-window"
                          type="radio"
                          value="Window"
                          checked={field.value === "Window"}
                          onChange={() => field.onChange("Window")}
                        />
                        <span className="checkmark"></span>
                      </div>
                    </label>
                  </div>
                )}
              />
            </div>
          </div>

          <div className="check-header-container">
            <p className="checkbox-header">{`Services Provided in Lease: `}</p>
          </div>

          <div className="checkbox-outer-container">
            <div className="property-type-container">
              <p className="checkbox-heading">Facilities</p>
              <Controller
                name="facility"
                control={control}
                defaultValue={[]}
                render={({ field }) => {
                  const ALL = [
                    "cam",
                    "furnished",
                    "janitorial",
                    "parking",
                    "tax",
                  ];
                  const allSelected = ALL.every((v) => field.value.includes(v));
                  const noneSelected = ALL.every(
                    (v) => !field.value.includes(v)
                  );
                  const someSelected = !allSelected && !noneSelected;

                  return (
                    <>
                      <div className="select-all">
                        <label
                          className="radio-container"
                          htmlFor="fac-select-all"
                        >
                          Select all
                          <div className="job-form-input-radio-check">
                            <input
                              id="fac-select-all"
                              type="checkbox"
                              checked={allSelected}
                              ref={(el) => {
                                if (el) el.indeterminate = someSelected;
                              }}
                              onChange={() => {
                                field.onChange(allSelected ? [] : ALL);
                              }}
                            />
                            <span className="checkmark"></span>
                          </div>
                        </label>
                      </div>
                      <div>
                        <label className="radio-container" htmlFor="fac-cam">
                          Common area maintenance
                          <div className="job-form-input-radio-check">
                            <input
                              id="fac-cam"
                              type="checkbox"
                              value="cam"
                              checked={field.value.includes("cam")}
                              // CAM
                              onChange={() => {
                                const newValue = field.value.includes("cam")
                                  ? field.value.filter((v) => v !== "cam")
                                  : [...field.value, "cam"];
                                field.onChange(newValue);
                                clearOverride("cam");
                              }}
                              onFocus={() => {
                                if (overrideState["cam"]) {
                                  setOverrideState((prev) => ({
                                    ...prev,
                                    cam: false,
                                  }));
                                }
                              }}
                            />
                            <span className="checkmark"></span>
                          </div>
                          <ToolTip
                            text={`Usually included in a desk rental or office lease. Unless the Lessee will be paying for CAM in addition to (outside of) the lease amount, this box should be checked as included.`}
                          >
                            <IoMdInformationCircleOutline />
                          </ToolTip>
                        </label>

                        <label
                          className="radio-container"
                          htmlFor="fac-furnished"
                        >
                          Furniture and furnishings
                          <div className="job-form-input-radio-check">
                            <input
                              id="fac-furnished"
                              type="checkbox"
                              value="furnished"
                              checked={field.value.includes("furnished")}
                              onChange={() => {
                                const newValue = field.value.includes(
                                  "furnished"
                                )
                                  ? field.value.filter((v) => v !== "furnished")
                                  : [...field.value, "furnished"];
                                field.onChange(newValue);
                              }}
                              onFocus={() => {
                                if (overrideState["furnished"]) {
                                  setOverrideState((prev) => ({
                                    ...prev,
                                    furnished: false,
                                  }));
                                }
                              }}
                            />
                            <span className="checkmark"></span>
                          </div>
                        </label>

                        <label
                          className="radio-container"
                          htmlFor="fac-janitorial"
                        >
                          Janitorial
                          <div className="job-form-input-radio-check">
                            <input
                              id="fac-janitorial"
                              type="checkbox"
                              value="janitorial"
                              checked={field.value.includes("janitorial")}
                              onChange={() => {
                                const newValue = field.value.includes(
                                  "janitorial"
                                )
                                  ? field.value.filter(
                                      (v) => v !== "janitorial"
                                    )
                                  : [...field.value, "janitorial"];
                                field.onChange(newValue);
                              }}
                            />
                            <span className="checkmark"></span>
                          </div>
                        </label>

                        <label
                          className="radio-container"
                          htmlFor="fac-parking"
                        >
                          Parking
                          <div className="job-form-input-radio-check">
                            <input
                              id="fac-parking"
                              type="checkbox"
                              value="parking"
                              checked={field.value.includes("parking")}
                              onChange={() => {
                                const newValue = field.value.includes("parking")
                                  ? field.value.filter((v) => v !== "parking")
                                  : [...field.value, "parking"];
                                field.onChange(newValue);
                              }}
                            />
                            <span className="checkmark"></span>
                          </div>
                        </label>

                        <label className="radio-container" htmlFor="fac-tax">
                          Property insurance and taxes
                          <div className="job-form-input-radio-check">
                            <input
                              id="fac-tax"
                              type="checkbox"
                              value="tax"
                              checked={field.value.includes("tax")}
                              // Taxes/Insurance
                              onChange={() => {
                                const newValue = field.value.includes("tax")
                                  ? field.value.filter((v) => v !== "tax")
                                  : [...field.value, "tax"];
                                field.onChange(newValue);
                                clearOverride("tax");
                              }}
                              onFocus={() => {
                                if (overrideState["tax"]) {
                                  setOverrideState((prev) => ({
                                    ...prev,
                                    tax: false,
                                  }));
                                }
                              }}
                            />
                            <span className="checkmark"></span>
                          </div>
                          <ToolTip
                            text={`Usually included in a desk rental or office lease. Unless the Lessee will be paying for Property Taxes and Property Insurance in addition to (outside of) the lease amount, this box should be checked as included.`}
                          >
                            <IoMdInformationCircleOutline />
                          </ToolTip>
                        </label>
                      </div>
                    </>
                  );
                }}
              />
            </div>

            <div className="space-type-container">
              <p className="checkbox-heading">Utilities</p>
              <Controller
                name="utility"
                control={control}
                defaultValue={[]}
                render={({ field }) => {
                  const ALL = [
                    "electricity",
                    "gas",
                    "water",
                    "phone",
                    "internet",
                  ];
                  const allSelected = ALL.every((v) => field.value.includes(v));
                  const noneSelected = ALL.every(
                    (v) => !field.value.includes(v)
                  );
                  const someSelected = !allSelected && !noneSelected;
                  return (
                    <>
                      <div className="select-all">
                        <label
                          className="radio-container"
                          htmlFor="util-select-all"
                        >
                          Select all
                          <div className="job-form-input-radio-check">
                            <input
                              id="util-select-all"
                              type="checkbox"
                              checked={allSelected}
                              ref={(el) => {
                                if (el) el.indeterminate = someSelected;
                              }}
                              onChange={() => {
                                field.onChange(allSelected ? [] : ALL);
                              }}
                            />
                            <span className="checkmark"></span>
                          </div>
                        </label>
                      </div>
                      <div>
                        <label
                          className="radio-container"
                          htmlFor="util-electricity"
                        >
                          Electricity
                          <div className="job-form-input-radio-check">
                            <input
                              id="util-electricity"
                              type="checkbox"
                              value="electricity"
                              checked={field.value.includes("electricity")}
                              onChange={() => {
                                const newValue = field.value.includes(
                                  "electricity"
                                )
                                  ? field.value.filter(
                                      (v) => v !== "electricity"
                                    )
                                  : [...field.value, "electricity"];
                                field.onChange(newValue);
                              }}
                            />
                            <span className="checkmark"></span>
                          </div>
                        </label>

                        <label className="radio-container" htmlFor="util-gas">
                          Gas/Oil/Propane
                          <div className="job-form-input-radio-check">
                            <input
                              id="util-gas"
                              type="checkbox"
                              value="gas"
                              checked={field.value.includes("gas")}
                              onChange={() => {
                                const newValue = field.value.includes("gas")
                                  ? field.value.filter((v) => v !== "gas")
                                  : [...field.value, "gas"];
                                field.onChange(newValue);
                              }}
                            />
                            <span className="checkmark"></span>
                          </div>
                        </label>

                        <label className="radio-container" htmlFor="util-water">
                          Water
                          <div className="job-form-input-radio-check">
                            <input
                              id="util-water"
                              type="checkbox"
                              value="water"
                              checked={field.value.includes("water")}
                              onChange={() => {
                                const newValue = field.value.includes("water")
                                  ? field.value.filter((v) => v !== "water")
                                  : [...field.value, "water"];
                                field.onChange(newValue);
                              }}
                            />
                            <span className="checkmark"></span>
                          </div>
                        </label>

                        <label className="radio-container" htmlFor="util-phone">
                          Phone
                          <div className="job-form-input-radio-check">
                            <input
                              id="util-phone"
                              type="checkbox"
                              value="phone"
                              checked={field.value.includes("phone")}
                              onChange={() => {
                                const newValue = field.value.includes("phone")
                                  ? field.value.filter((v) => v !== "phone")
                                  : [...field.value, "phone"];
                                field.onChange(newValue);
                              }}
                            />
                            <span className="checkmark"></span>
                          </div>
                        </label>

                        <label
                          className="radio-container"
                          htmlFor="util-internet"
                        >
                          Internet/WiFi
                          <div className="job-form-input-radio-check">
                            <input
                              id="util-internet"
                              type="checkbox"
                              value="internet"
                              checked={field.value.includes("internet")}
                              onChange={() => {
                                const newValue = field.value.includes(
                                  "internet"
                                )
                                  ? field.value.filter((v) => v !== "internet")
                                  : [...field.value, "internet"];
                                field.onChange(newValue);
                              }}
                            />
                            <span className="checkmark"></span>
                          </div>
                        </label>
                      </div>
                    </>
                  );
                }}
              />
            </div>

            <div className="setting-type-container">
              <p className="checkbox-heading">Amenities</p>
              <Controller
                name="amenity"
                control={control}
                defaultValue={[]}
                render={({ field }) => {
                  const ALL = ["meeting", "kitchen", "copy", "receptionist"];
                  const allSelected = ALL.every((v) => field.value.includes(v));
                  const noneSelected = ALL.every(
                    (v) => !field.value.includes(v)
                  );
                  const someSelected = !allSelected && !noneSelected;
                  return (
                    <>
                      <div className="select-all">
                        <label
                          className="radio-container"
                          htmlFor="amen-select-all"
                        >
                          Select all
                          <div className="job-form-input-radio-check">
                            <input
                              id="amen-select-all"
                              type="checkbox"
                              checked={allSelected}
                              ref={(el) => {
                                if (el) el.indeterminate = someSelected;
                              }}
                              onChange={() =>
                                field.onChange(allSelected ? [] : ALL)
                              }
                            />
                            <span className="checkmark"></span>
                          </div>
                        </label>
                      </div>
                      <div>
                        <label
                          className="radio-container"
                          htmlFor="amen-meeting"
                        >
                          Furnished conference room use
                          <div className="job-form-input-radio-check">
                            <input
                              id="amen-meeting"
                              type="checkbox"
                              value="meeting"
                              checked={field.value.includes("meeting")}
                              onChange={() => {
                                const newValue = field.value.includes("meeting")
                                  ? field.value.filter((v) => v !== "meeting")
                                  : [...field.value, "meeting"];
                                field.onChange(newValue);
                                clearOverride("meeting");
                              }}
                              onFocus={() => {
                                if (overrideState["meeting"]) {
                                  setOverrideState((prev) => ({
                                    ...prev,
                                    meeting: false,
                                  }));
                                }
                              }}
                            />
                            <span className="checkmark"></span>
                          </div>
                        </label>

                        <label
                          className="radio-container"
                          htmlFor="amen-kitchen"
                        >
                          Kitchen
                          <div className="job-form-input-radio-check">
                            <input
                              id="amen-kitchen"
                              type="checkbox"
                              value="kitchen"
                              checked={field.value.includes("kitchen")}
                              onChange={() => {
                                const newValue = field.value.includes("kitchen")
                                  ? field.value.filter((v) => v !== "kitchen")
                                  : [...field.value, "kitchen"];
                                field.onChange(newValue);
                                clearOverride("kitchen");
                              }}
                              onFocus={() => {
                                if (overrideState["kitchen"]) {
                                  setOverrideState((prev) => ({
                                    ...prev,
                                    kitchen: false,
                                  }));
                                }
                              }}
                            />
                            <span className="checkmark"></span>
                          </div>
                        </label>

                        <label className="radio-container" htmlFor="amen-copy">
                          Copy/fax/printer/scanner use
                          <div className="job-form-input-radio-check">
                            <input
                              id="amen-copy"
                              type="checkbox"
                              value="copy"
                              checked={field.value.includes("copy")}
                              onChange={() => {
                                const newValue = field.value.includes("copy")
                                  ? field.value.filter((v) => v !== "copy")
                                  : [...field.value, "copy"];
                                field.onChange(newValue);
                                clearOverride("copy");
                              }}
                              onFocus={() => {
                                if (overrideState["copy"]) {
                                  setOverrideState((prev) => ({
                                    ...prev,
                                    copy: false,
                                  }));
                                }
                              }}
                            />
                            <span className="checkmark"></span>
                          </div>
                        </label>

                        <label
                          className="radio-container"
                          htmlFor="amen-receptionist"
                        >
                          Receptionist/Answering
                          <div className="job-form-input-radio-check">
                            <input
                              id="amen-receptionist"
                              type="checkbox"
                              value="receptionist"
                              checked={field.value.includes("receptionist")}
                              onChange={() => {
                                const newValue = field.value.includes(
                                  "receptionist"
                                )
                                  ? field.value.filter(
                                      (v) => v !== "receptionist"
                                    )
                                  : [...field.value, "receptionist"];
                                field.onChange(newValue);
                                clearOverride("receptionist");
                              }}
                              onFocus={() => {
                                if (overrideState["receptionist"]) {
                                  setOverrideState((prev) => ({
                                    ...prev,
                                    receptionist: false,
                                  }));
                                }
                              }}
                            />
                            <span className="checkmark"></span>
                          </div>
                        </label>
                      </div>
                    </>
                  );
                }}
              />
            </div>
          </div>

          <div className="edit-image-review-container">
            <p className="checkbox-header upload-header">{`Current images: `}</p>
            <div className="edit-image-review">
              {fileUrls.map((url) => (
                <div className="edit-image-container" key={url}>
                  <img src={url} alt="Exhibit" className="edit-image" />
                  <button
                    type="button"
                    className="remove-button"
                    onClick={() => removeImage(url)}
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="checkbox-outer-container">
            <div className="property-type-container">
              <p className="checkbox-header upload-header">{`Upload images: `}</p>
            </div>
          </div>

          <input type="file" onChange={handleChange} multiple />

          <div className="button-container">
            {!isPartner && (
              <button
                type="button"
                className="submit-btn exhibit-save"
                onClick={async () => {
                  // If nothing changed, just go back
                  if (!hasUnsavedChanges) {
                    goToDashboardAfterSave();
                    return;
                  }

                  const data = getValues();
                  const ok = await onSubmit(data, "Saved");
                  if (ok) {
                    // optional: clear image-dirty flag after a successful save on this page
                    setImagesDirty(false);
                    goToDashboardAfterSave();
                  }
                }}
                title={saveExitLabel}
                aria-label={saveExitLabel}
              >
                {saveExitLabel}
              </button>
            )}

            <div
              className={Object.keys(errors).length > 0 ? "error" : ""}
              style={{ display: "flex", flexDirection: "column" }}
            >
              {Object.keys(errors).length > 0 && (
                <p className="error-message submit-error">
                  {" "}
                  <span style={{ marginRight: "5px" }}>
                    <FaExclamationCircle />
                  </span>
                  Please fix the errors above before continuing.
                </p>
              )}
              <button
                className="submit-btn exhibit-submit"
                type="button"
                onClick={handleSubmit(async (data) => {
                  dispatch(setLoading(true));
                  try {
                    const ok = await onSubmit(data, "In Progress"); // confirmations enabled
                    if (ok) {
                      const nav = buildReviewURL(
                        {
                          id: exhibit.id,
                          companyName: exhibit.companyName,
                          commonName: exhibit.commonName,
                          serviceProvider: exhibit.serviceProvider,
                        },
                        true
                      );
                      navigate(nav.path, { state: nav.state });
                    }
                  } finally {
                    dispatch(setLoading(false));
                  }
                })}
              >
                {!isPartner ? "Save & Continue" : "Continue"}
              </button>
            </div>
          </div>
        </form>
      </div>
      {isDirty && (
        <div className="clear-btn-wrapper">
          <button
            type="button"
            className="clear-btn"
            onClick={() => setShowClearConfirm(true)}
          >
            Clear All
          </button>
        </div>
      )}
      {showClearConfirm && (
        <div
          className="modal-container"
          role="dialog"
          aria-modal="true"
          aria-labelledby="clear-confirm-title"
        >
          <div className="my-modal">
            <h2 id="clear-confirm-title" className="modal-title">
              Clear all fields?
            </h2>

            <p className="modal-text">
              This will reset every field in the form.
            </p>

            <p className="modal-subtext">This action cannot be undone.</p>

            <div className="modal-btn-cont">
              <button
                type="button"
                className="modal-cancel"
                onClick={() => setShowClearConfirm(false)}
              >
                Cancel
              </button>

              <button
                type="button"
                className="modal-delete"
                onClick={() => {
                  setShowClearConfirm(false);
                  handleClearAll();
                }}
              >
                Clear all
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EditForm;
